import React from "react";
import {
  QuestionnaireSection,
  QuestionnaireResponses,
  QuestionnaireQuestion,
} from "services/questionnaireService";
import {
  SingleSelectionQuestion,
  MultipleSelectionQuestion,
  TextInputQuestion,
  TextAreaQuestion,
  ScaleQuestion,
  MultipleOptionsQuestion,
  CheckboxQuestion,
  SingleChoiceMixedQuestion,
  TextListQuestion,
} from "./QuestionTypes";

interface QuestionSectionProps {
  section: QuestionnaireSection;
  responses: QuestionnaireResponses;
  handleResponseChange: (
    sectionId: string,
    questionId: string,
    value: string | number
  ) => void;
  handleTextAreaChange: (
    sectionId: string,
    questionId: string,
    value: string
  ) => void;
  handleMultipleResponseChange: (
    sectionId: string,
    questionId: string,
    optionValue: string,
    isChecked: boolean
  ) => void;
  handleMultipleSelectionChange: (
    sectionId: string,
    questionId: string,
    checked: boolean
  ) => void;
  handleScaleChange: (
    sectionId: string,
    questionId: string,
    value: number
  ) => void;
}

const QuestionSection: React.FC<QuestionSectionProps> = ({
  section,
  responses,
  handleResponseChange,
  handleTextAreaChange,
  handleMultipleResponseChange,
  handleMultipleSelectionChange,
  handleScaleChange,
}) => {
  console.log(
    `Rendering section ${section.id} with response type: ${section.response_type}`
  );

  // Add text_list section type handling
  if (section.response_type === "text_list") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-800">
        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
          {section.title}
        </h3>
        {section.description && (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {section.description}
          </p>
        )}
        <TextListQuestion
          sectionId={section.id}
          questions={section.questions}
          response={responses[section.id][section.questions[0].id]}
          onResponseChange={handleResponseChange}
          minItems={section.min_items || 5}
          maxItems={section.max_items || 20}
        />
      </div>
    );
  }

  // Render single_choice_mixed questions with options at the question level
  if (section.response_type === "single_choice_mixed") {
    console.log("Rendering single_choice_mixed section:", section);
    return (
      <div className="space-y-5 md:space-y-8">
        {section.questions.map((question) => {
          // Type assertion to ensure response is compatible with SingleChoiceMixedQuestion
          const response = responses[section.id][question.id] as
            | string
            | number
            | null;

          return (
            <div
              key={question.id}
              className="rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:p-4"
            >
              <p className="mb-3 text-base font-medium text-gray-800 dark:text-white md:mb-4 md:text-lg">
                {question.order}. {question.text}
              </p>

              <SingleChoiceMixedQuestion
                question={question}
                sectionId={section.id}
                response={response}
                onResponseChange={handleResponseChange}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // Render mixed questions with different input types
  if (section.response_type === "mixed") {
    return (
      <div className="space-y-4 md:space-y-6">
        {section.questions.map((question) => {
          const response = responses[section.id][question.id];

          return (
            <div
              key={question.id}
              className="rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:p-4"
            >
              <p className="mb-3 text-base font-medium text-gray-800 dark:text-white md:mb-4 md:text-lg">
                {question.order}. {question.text}
              </p>

              {question.input_type === "single_selection" && (
                <SingleSelectionQuestion
                  question={question}
                  sectionId={section.id}
                  response={response}
                  onResponseChange={handleResponseChange}
                />
              )}

              {question.input_type === "multiple_selection" && (
                <MultipleSelectionQuestion
                  question={question}
                  sectionId={section.id}
                  response={response}
                  onMultipleResponseChange={handleMultipleResponseChange}
                />
              )}

              {question.input_type === "text" && (
                <TextInputQuestion
                  question={question}
                  sectionId={section.id}
                  response={response}
                  onResponseChange={handleResponseChange}
                />
              )}

              {question.input_type === "text_area" && (
                <TextAreaQuestion
                  question={question}
                  sectionId={section.id}
                  response={response}
                  onTextAreaChange={handleTextAreaChange}
                />
              )}

              {question.input_type === "scale_1_10" && (
                <ScaleQuestion
                  question={question}
                  sectionId={section.id}
                  response={response}
                  onScaleChange={handleScaleChange}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Render text_area questions with textarea inputs
  if (section.response_type === "text_area") {
    return (
      <div className="space-y-5 md:space-y-8">
        {section.questions.map((question) => {
          const currentValue = responses[section.id][question.id] as string;

          return (
            <div
              key={question.id}
              className="rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:p-4"
            >
              <p className="mb-3 text-base font-medium text-gray-800 dark:text-white md:mb-4 md:text-lg">
                {question.order}. {question.text}
              </p>

              <TextAreaQuestion
                question={question}
                sectionId={section.id}
                response={currentValue}
                onTextAreaChange={handleTextAreaChange}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // Render scale_1_10 questions with range slider and number buttons
  if (section.response_type === "scale_1_10") {
    return (
      <div className="space-y-5 md:space-y-8">
        {section.questions.map((question) => {
          const currentValue = responses[section.id][question.id] as number;

          return (
            <div
              key={question.id}
              className="rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:p-4"
            >
              <p className="mb-3 text-base font-medium text-gray-800 dark:text-white md:mb-4 md:text-lg">
                {question.order}. {question.text}
              </p>

              <ScaleQuestion
                question={question}
                sectionId={section.id}
                response={currentValue}
                onScaleChange={handleScaleChange}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // Render multiple_selection questions with checkboxes
  if (section.response_type === "multiple_selection") {
    return (
      <div className="space-y-2 md:space-y-3">
        {section.questions.map((question) => (
          <CheckboxQuestion
            key={question.id}
            question={question}
            sectionId={section.id}
            response={responses[section.id][question.id]}
            onMultipleSelectionChange={handleMultipleSelectionChange}
          />
        ))}
      </div>
    );
  }

  // Render other question types
  return (
    <>
      {section.questions.map((question) => (
        <div
          key={question.id}
          className="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:mb-5 md:p-4"
        >
          <p className="mb-3 text-base font-medium text-gray-800 dark:text-white md:mb-4 md:text-lg">
            {question.order}. {question.text}
          </p>

          {section.response_type === "single_choice" &&
            (() => {
              console.log("Options for single_choice:", section.options);
              return (
                <div className="space-y-2 md:space-y-3">
                  {section.options?.map((option) => {
                    const isSelected =
                      responses[section.id][question.id] === option.value;
                    return (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer items-center rounded-lg border p-2 transition-colors duration-200 md:p-3 ${
                          isSelected
                            ? "border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-navy-900"
                            : "border-gray-200 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option.value}
                          checked={isSelected}
                          onChange={() => {
                            // Convert to number if the option value is numeric
                            const value =
                              typeof option.value === "string" &&
                              !isNaN(Number(option.value))
                                ? Number(option.value)
                                : option.value;
                            console.log(
                              `Selected option: ${
                                option.label
                              }, value: ${value}, type: ${typeof value}`
                            );
                            console.log(
                              `Current response: ${
                                responses[section.id][question.id]
                              }, Checked: ${
                                responses[section.id][question.id] ===
                                option.value
                              }`
                            );
                            handleResponseChange(
                              section.id,
                              question.id,
                              value
                            );
                          }}
                          className="h-4 w-4 text-brand-500 focus:ring-brand-400 md:h-5 md:w-5"
                        />
                        <span
                          className={`ml-2 text-sm md:ml-3 md:text-base ${
                            isSelected
                              ? "font-medium text-brand-600 dark:text-brand-400"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              );
            })()}

          {section.response_type === "multiple_options" && (
            <MultipleOptionsQuestion
              question={question}
              sectionId={section.id}
              response={responses[section.id][question.id]}
              onMultipleResponseChange={handleMultipleResponseChange}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default QuestionSection;
